import { findConsecutiveRepeatedCharacter, findRepeatedWord } from './descriptionValidation';

export interface CurrencyExistingRecord {
  _id?: string;
  code?: string;
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}

export interface CurrencyFormInput {
  code: string;
  name: string;
  description?: string;
  sortOrder: number;
  metadata: Record<string, string | number | boolean>;
}

export interface CurrencyValidationContext {
  existing: CurrencyExistingRecord[];
  editingId?: string;
  baseCurrency: string;
  isoNumeric?: string;
  autoRateSupported: boolean;
}

export const CURRENCY_FIELD_LABELS: Record<string, string> = {
  code: 'Currency',
  name: 'Currency name',
  isoNumeric: 'ISO numeric code',
  symbol: 'Symbol',
  decimalPlaces: 'Decimal places',
  decimalSeparator: 'Decimal separator',
  thousandSeparator: 'Thousand separator',
  currencyPosition: 'Symbol position',
  exchangeRate: 'Exchange rate',
  exchangeRateSource: 'Rate source',
  autoUpdate: 'Auto update rate',
  roundingRule: 'Rounding rule',
  cashRounding: 'Cash rounding',
  minPayment: 'Minimum payment',
  maxPayment: 'Maximum payment',
  sortOrder: 'Sort order',
  description: 'Description',
};

export const CURRENCY_MAX_SORT_ORDER = 9999;
export const CURRENCY_MAX_DECIMALS = 4;
export const CURRENCY_MAX_RATE = 1_000_000;
const MAX_AMOUNT = 1_000_000_000_000;

export const DECIMAL_SEPARATOR_OPTIONS = [
  { value: '.', label: 'Period  .' },
  { value: ',', label: 'Comma  ,' },
];
export const THOUSAND_SEPARATOR_OPTIONS = [
  { value: ',', label: 'Comma  ,' },
  { value: '.', label: 'Period  .' },
  { value: ' ', label: 'Space' },
  { value: "'", label: "Apostrophe  '" },
];
export const SYMBOL_POSITION_OPTIONS = [
  { value: 'before', label: 'Before amount' },
  { value: 'after', label: 'After amount' },
];
export const ROUNDING_RULE_OPTIONS = [
  { value: 'half_up', label: 'Half up', description: 'Halves round away from zero — the usual commercial rule.' },
  { value: 'half_down', label: 'Half down', description: 'Halves round toward zero; anything above half rounds up.' },
  { value: 'bankers', label: 'Bankers (half even)', description: 'Halves round to the nearest even digit to avoid bias over many amounts.' },
  { value: 'floor', label: 'Floor', description: 'Always rounds down — the customer never pays the fraction.' },
];

const NAME_PATTERN = /^[\p{L}\p{M}][\p{L}\p{M} .'()-]*$/u;
const SYMBOL_PATTERN = /^(?!.*\d)(?!.*\s{2})\S(?:.{0,4}\S)?$/u;
const DENOMINATION_MANTISSAS = [1, 2, 2.5, 5];

const str = (v: unknown) => (v == null ? '' : String(v).trim());
const isBlank = (v: unknown) => str(v) === '';
export const normalizeCurrencyName = (s: unknown) => String(s ?? '').replace(/\s+/g, ' ').trim();

export function decimalsOf(value: number): number {
  const text = String(value);
  if (/e-/i.test(text)) return Number(text.split(/e-/i)[1]) || 0;
  return (text.split('.')[1] || '').length;
}

export function nextCurrencySortOrder(records: CurrencyExistingRecord[], excludeId?: string) {
  return records
    .filter((r) => r._id !== excludeId)
    .reduce((max, r) => Math.max(max, Number(r.sortOrder) || 0), 0) + 1;
}

/** Same rounding the server applies to converted amounts. */
export function roundWithRule(amount: number, decimals: number, rule: string): number {
  const factor = 10 ** decimals;
  const scaled = Number((amount * factor).toFixed(8));
  if (rule === 'floor') return Math.floor(scaled) / factor;
  const floor = Math.floor(scaled);
  const isHalf = Math.abs(scaled - floor - 0.5) < 1e-9;
  if (rule === 'half_down') return (isHalf ? floor : Math.round(scaled)) / factor;
  if (rule === 'bankers') return (isHalf ? (floor % 2 === 0 ? floor : floor + 1) : Math.round(scaled)) / factor;
  return (isHalf ? floor + 1 : Math.round(scaled)) / factor;
}

export interface CurrencyFormat {
  symbol: string;
  decimals: number;
  decimalSeparator: string;
  thousandSeparator: string;
  position: string;
}

/** Mirrors the server formatter (3-digit grouping). */
export function formatCurrencyAmount(amount: number, f: CurrencyFormat): string {
  const decimals = Number.isInteger(f.decimals) && f.decimals >= 0 ? Math.min(f.decimals, CURRENCY_MAX_DECIMALS) : 2;
  const fixed = Math.abs(amount).toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, f.thousandSeparator || ',');
  const body = decPart ? `${grouped}${f.decimalSeparator || '.'}${decPart}` : grouped;
  const sign = amount < 0 ? '-' : '';
  const symbol = f.symbol || '';
  return f.position === 'after' ? `${sign}${body} ${symbol}`.trim() : `${sign}${symbol}${body}`;
}

export function formatFromMetadata(meta: Record<string, unknown> | undefined, code = ''): CurrencyFormat {
  const decimals = Number(meta?.decimalPlaces);
  return {
    symbol: str(meta?.symbol) || code,
    decimals: Number.isInteger(decimals) ? decimals : 2,
    decimalSeparator: str(meta?.decimalSeparator) || '.',
    thousandSeparator: meta?.thousandSeparator === ' ' ? ' ' : str(meta?.thousandSeparator) || ',',
    position: str(meta?.currencyPosition) || 'before',
  };
}

export function isValidDenomination(value: number): boolean {
  if (!(value > 0)) return false;
  const exponent = Math.floor(Math.log10(value));
  for (const e of [exponent - 1, exponent]) {
    const mantissa = Number((value / 10 ** e).toPrecision(6));
    if (DENOMINATION_MANTISSAS.includes(mantissa)) return true;
  }
  return false;
}

/** Common coin / note steps that fit the currency precision. */
export function cashRoundingSuggestions(decimals: number): number[] {
  const all = [0.01, 0.05, 0.1, 0.25, 0.5, 1, 5, 10];
  return all.filter((v) => decimalsOf(v) <= decimals).slice(0, 6);
}

function repeatedTextError(label: string, text: string) {
  const run = findConsecutiveRepeatedCharacter(text);
  if (run) return `${label} cannot repeat the same character 4+ times ("${run.slice(0, 4)}")`;
  const word = findRepeatedWord(text);
  if (word) return `${label} cannot repeat the same word back to back ("${word}")`;
  return undefined;
}

function amountError(label: string, raw: unknown, decimals: number, opts: { positive?: boolean } = {}) {
  const value = Number(raw);
  if (!Number.isFinite(value)) return `${label} must be a number`;
  if (value < 0 || (opts.positive && value === 0)) return `${label} must be ${opts.positive ? 'greater than 0' : '0 or more'}`;
  if (value > MAX_AMOUNT) return `${label} is too large`;
  if (decimalsOf(value) > decimals) {
    return decimals === 0
      ? `${label} must be a whole amount — this currency has no decimal places`
      : `${label} can have at most ${decimals} decimal place${decimals === 1 ? '' : 's'}`;
  }
  return undefined;
}

/** Field errors keyed by field name (metadata keys are flattened). */
export function validateCurrencyForm(form: CurrencyFormInput, ctx: CurrencyValidationContext): Record<string, string> {
  const errors: Record<string, string> = {};
  const meta = form.metadata;
  const code = str(form.code).toUpperCase();
  const others = ctx.existing.filter((r) => r._id !== ctx.editingId);
  const isBase = !!code && code === ctx.baseCurrency;

  if (!/^[A-Z]{3}$/.test(code)) errors.code = 'Select a currency from the ISO 4217 list';
  else if (others.some((r) => str(r.code).toUpperCase() === code)) errors.code = `${code} is already added`;

  const name = normalizeCurrencyName(form.name);
  if (!name) errors.name = 'Currency name is required';
  else if (name.length < 2 || name.length > 60) errors.name = 'Currency name must be 2–60 characters';
  else if (!NAME_PATTERN.test(name)) errors.name = 'Start with a letter; use letters, spaces or . \' ( ) -';
  else {
    const repeat = repeatedTextError('Currency name', name);
    if (repeat) errors.name = repeat;
    else {
      const clash = others.find((r) => normalizeCurrencyName(r.name).toLowerCase() === name.toLowerCase());
      if (clash) errors.name = `"${name}" already exists (code ${clash.code})`;
    }
  }

  const numeric = str(meta.isoNumeric);
  if (!/^\d{3}$/.test(numeric)) errors.isoNumeric = 'ISO numeric code must be exactly 3 digits';
  else if (ctx.isoNumeric && numeric !== ctx.isoNumeric) errors.isoNumeric = `ISO numeric code for ${code} is ${ctx.isoNumeric}`;
  else {
    const clash = others.find((r) => str(r.metadata?.isoNumeric) === numeric);
    if (clash) errors.isoNumeric = `${numeric} is already used by ${clash.code}`;
  }

  const symbol = str(meta.symbol);
  if (!symbol) errors.symbol = 'Symbol is required';
  else if (!SYMBOL_PATTERN.test(symbol)) errors.symbol = '1–6 characters, no digits or double spaces (e.g. ₹, $, CHF)';

  const decimals = Number(meta.decimalPlaces);
  const decimalsValid = !isBlank(meta.decimalPlaces) && Number.isInteger(decimals) && decimals >= 0 && decimals <= CURRENCY_MAX_DECIMALS;
  if (isBlank(meta.decimalPlaces)) errors.decimalPlaces = 'Decimal places is required';
  else if (!decimalsValid) errors.decimalPlaces = `Whole number from 0 to ${CURRENCY_MAX_DECIMALS}`;
  const precision = decimalsValid ? decimals : CURRENCY_MAX_DECIMALS;

  const decSep = str(meta.decimalSeparator) || '.';
  const thouSep = meta.thousandSeparator === ' ' ? ' ' : str(meta.thousandSeparator) || ',';
  if (decSep === thouSep) errors.thousandSeparator = 'Must be different from the decimal separator';

  const autoUpdate = meta.autoUpdate === true;
  const source = str(meta.exchangeRateSource) || 'manual';
  if (!isBase) {
    if (autoUpdate && source !== 'api') errors.autoUpdate = 'Choose the Frankfurter API rate source to enable auto update';
    else if (autoUpdate && !ctx.autoRateSupported) errors.autoUpdate = `Automatic rates are not published for ${code} / ${ctx.baseCurrency}`;
    if (isBlank(meta.exchangeRate)) {
      if (!autoUpdate) errors.exchangeRate = `Enter how many ${code || 'units'} equal 1 ${ctx.baseCurrency}`;
    } else {
      const rate = Number(meta.exchangeRate);
      if (!Number.isFinite(rate) || rate <= 0) errors.exchangeRate = 'Exchange rate must be greater than 0';
      else if (rate > CURRENCY_MAX_RATE) errors.exchangeRate = `At most ${CURRENCY_MAX_RATE.toLocaleString('en-US')}`;
      else if (decimalsOf(rate) > 8) errors.exchangeRate = 'At most 8 decimal places';
    }
  }

  if (!isBlank(meta.cashRounding) && Number(meta.cashRounding) !== 0) {
    const err = amountError('Cash rounding', meta.cashRounding, precision, { positive: true });
    if (err) errors.cashRounding = err;
    else if (Number(meta.cashRounding) > 1000 || !isValidDenomination(Number(meta.cashRounding))) {
      errors.cashRounding = 'Use a coin or note value such as 0.05, 0.10, 0.25, 0.50, 1, 5 or 10';
    }
  }

  const min = isBlank(meta.minPayment) ? 0 : Number(meta.minPayment);
  const minErr = amountError('Minimum payment', min, precision);
  if (minErr) errors.minPayment = minErr;
  if (!isBlank(meta.maxPayment) && Number(meta.maxPayment) !== 0) {
    const maxErr = amountError('Maximum payment', meta.maxPayment, precision, { positive: true });
    if (maxErr) errors.maxPayment = maxErr;
    else if (!minErr && Number(meta.maxPayment) < min) errors.maxPayment = 'Must be greater than or equal to the minimum payment';
  }

  const order = Number(form.sortOrder);
  if (!Number.isInteger(order) || order < 1 || order > CURRENCY_MAX_SORT_ORDER) {
    errors.sortOrder = `Enter a whole number between 1 and ${CURRENCY_MAX_SORT_ORDER}`;
  } else {
    const clash = others.find((r) => Number(r.sortOrder) === order);
    if (clash) {
      errors.sortOrder = `Sort order ${order} is already used by ${clash.name} — next available is ${nextCurrencySortOrder(ctx.existing, ctx.editingId)}`;
    }
  }

  const description = str(form.description);
  if (description.length > 500) errors.description = 'Description must be at most 500 characters';
  else if (description) {
    const repeat = repeatedTextError('Description', description);
    if (repeat) errors.description = repeat;
  }
  return errors;
}
